/**
 * ExperienceMap is responsible for rendering and controlling
 * interactive hotspots on the hero illustration.
 */
import gsap                from 'gsap';
import experienceMapConfig from '../config/experienceMap.js';

export default class ExperienceMap {
    constructor({
        rootElement,
        sectionId
    }) {
        this.rootElement              = rootElement;
        this.sectionId                = sectionId;
        this.sectionConfig            = null;
        this.items                    = [];
        this.tooltipElement           = null;
        this.tooltipTitleElement      = null;
        this.tooltipTextElement       = null;
        this.tooltipCloseElement      = null;
        this.isMobile                 = false;
        this.resizeHandler            = null;
        this.activeItemElement        = null;
        this.pointerMoveHandler       = null;
        this.outsideClickHandler      = null;
        this.discoveredItemIds        = new Set();
        this.totalInteractiveItems    = 0;
    }

    init() {
        try {
            if (!this.rootElement || !this.sectionId) {
                return;
            }

            this.sectionConfig = experienceMapConfig.sections.find((section) => section.id === this.sectionId);

            if (!this.sectionConfig) {
                return;
            }

            this.cacheTooltipElements();
            this.handleResize();
            this.initItems();
            this.bindResizeListener();
            this.bindGlobalEvents();
        } catch (error) {
            // eslint-disable-next-line no-console
            console.error('ExperienceMap init error', error);
        }
    }

    destroy() {
        if (this.resizeHandler) {
            window.removeEventListener('resize', this.resizeHandler);
        }

        if (this.pointerMoveHandler) {
            window.removeEventListener('mousemove', this.pointerMoveHandler);
        }

        if (this.outsideClickHandler) {
            document.removeEventListener('click', this.outsideClickHandler);
        }
    }

    cacheTooltipElements() {
        this.tooltipElement      = this.rootElement.querySelector('[data-map-tooltip]');
        this.tooltipTitleElement = this.rootElement.querySelector('[data-map-tooltip-title]');
        this.tooltipTextElement  = this.rootElement.querySelector('[data-map-tooltip-text]');
        this.tooltipCloseElement = this.rootElement.querySelector('[data-map-tooltip-close]');

        if (this.tooltipCloseElement) {
            this.tooltipCloseElement.addEventListener('click', (event) => {
                event.preventDefault();
                event.stopPropagation();
                this.hideTooltip();
                this.clearActiveState();
            });
        }
    }

    handleResize() {
        const { breakpointMobileMax } = experienceMapConfig;
        this.isMobile               = window.innerWidth <= breakpointMobileMax;
    }

    bindResizeListener() {
        this.resizeHandler = () => {
            const previousIsMobile = this.isMobile;
            this.handleResize();

            if (previousIsMobile !== this.isMobile) {
                this.updateItemPositions();
            }
        };

        window.addEventListener('resize', this.resizeHandler);
    }

    bindGlobalEvents() {
        this.outsideClickHandler = (event) => {
            if (!this.tooltipElement) {
                return;
            }

            const clickTarget     = event.target;
            const isInsideTooltip = this.tooltipElement.contains(clickTarget);
            const isInsideItem    = clickTarget.closest('[data-map-item-id]');

            if (!isInsideTooltip && !isInsideItem) {
                this.hideTooltip();
                this.clearActiveState();
            }
        };
        document.addEventListener('click', this.outsideClickHandler);
    }

    initItems() {
        if (!this.sectionConfig || !Array.isArray(this.sectionConfig.items)) {
            return;
        }

        this.items = this.sectionConfig.items.map((itemConfig) => this.initSingleItem({ itemConfig }));
        this.updateItemPositions();

        this.totalInteractiveItems = this.sectionConfig.items.filter((itemConfig) => {
            if (!itemConfig) {
                return false;
            }

            const isInteractive      = itemConfig.isInteractive !== false;
            const isInitiallyVisible = itemConfig.initiallyVisible !== false;

            return isInteractive && isInitiallyVisible;
        }).length;
    }

    initSingleItem({ itemConfig }) {
        const itemElement = this.rootElement.querySelector(`[data-map-item-id="${itemConfig.id}"]`);

        if (!itemElement) {
            return null;
        }

        if (!itemConfig.initiallyVisible) {
            itemElement.classList.add('experience-map__item--hidden');
        }

        const clipPathValue = `url("#experience-map-clip-${this.sectionId}-${itemConfig.id}")`;

        itemElement.style.setProperty('--experience-map-clip-path', clipPathValue);

        if (itemConfig.label) {
            itemElement.setAttribute('aria-label', itemConfig.label);
        }

        this.bindItemEvents({
            itemElement,
            itemConfig
        });

        return {
            config  : itemConfig,
            element : itemElement
        };
    }

    updateItemPositions() {
        this.items.forEach((item) => {
            if (!item || !item.element) {
                return;
            }

            const position = this.isMobile
                ? item.config.mobilePosition
                : item.config.desktopPosition;

            if (!position) {
                return;
            }

            const hasPercent = (value) => typeof value === 'number' || typeof value === 'string';

            const {
                leftPercent,
                topPercent,
                widthPercent,
                rightPercent,
                bottomPercent
            } = position;

            const { style } = item.element;

            if (hasPercent(widthPercent)) {
                style.width = `${widthPercent}%`;
            }

            if (hasPercent(leftPercent) || hasPercent(rightPercent)) {
                style.left  = '';
                style.right = '';

                if (hasPercent(leftPercent)) {
                    style.left = `${leftPercent}%`;
                } else if (hasPercent(rightPercent)) {
                    style.right = `${rightPercent}%`;
                }
            }

            if (hasPercent(topPercent) || hasPercent(bottomPercent)) {
                style.top    = '';
                style.bottom = '';

                if (hasPercent(topPercent)) {
                    style.top = `${topPercent}%`;
                } else if (hasPercent(bottomPercent)) {
                    style.bottom = `${bottomPercent}%`;
                }
            }
        });
    }

    bindItemEvents({
        itemElement,
        itemConfig
    }) {
        if (!itemElement) {
            return;
        }

        itemElement.addEventListener('mouseleave', () => {
            if (this.isMobile) {
                this.hideTooltip();
                this.clearActiveState();
            }
        });

        itemElement.addEventListener('click', (event) => {
            this.handleItemActivate({
                itemConfig,
                element      : itemElement,
                triggerEvent : event
            });
        });
    }

    handleItemHover({
        itemConfig,
        element
    }) {
        this.setItemActiveState({
            element,
            isActive : true
        });
        this.showTooltipForItem({
            itemConfig,
            element
        });
    }

    handleItemActivate({
        itemConfig,
        element,
        triggerEvent
    }) {
        if (!element) {
            return;
        }

        this.setActiveItem({
            itemConfig,
            element
        });

        this.registerDiscoveredItem(itemConfig);

        this.showTooltipForItem({
            itemConfig,
            element,
            triggerEvent
        });
    }

    setActiveItem({
        itemConfig,
        element
    }) {
        if (this.activeItemElement && this.activeItemElement !== element) {
            this.setItemActiveState({
                element  : this.activeItemElement,
                isActive : false
            });
        }

        this.activeItemConfig  = itemConfig;
        this.activeItemElement = element;

        this.setItemActiveState({
            element,
            isActive : true
        });
    }

    clearActiveState() {
        if (this.activeItemElement) {
            this.setItemActiveState({
                element  : this.activeItemElement,
                isActive : false
            });
        }

        this.activeItemElement        = null;
        this.activeItemConfig         = null;
        this.isTooltipFollowingCursor = false;
    }

    setItemActiveState({
        element,
        isActive
    }) {
        if (!element) {
            return;
        }

        if (isActive) {
            element.classList.add('experience-map__item--active');
        } else {
            element.classList.remove('experience-map__item--active');
        }
    }

    registerDiscoveredItem(itemConfig) {
        if (!itemConfig) {
            return;
        }

        const isInteractive      = itemConfig.isInteractive !== false;
        const isInitiallyVisible = itemConfig.initiallyVisible !== false;

        if (!isInteractive || !isInitiallyVisible) {
            return;
        }

        if (this.discoveredItemIds.has(itemConfig.id)) {
            return;
        }

        this.discoveredItemIds.add(itemConfig.id);

        if (this.totalInteractiveItems > 0 && this.discoveredItemIds.size === this.totalInteractiveItems) {
            const event = new CustomEvent('experience-map:complete', {
                detail : {
                    sectionId : this.sectionId,
                    total     : this.totalInteractiveItems
                }
            });

            window.dispatchEvent(event);
        }
    }

    revealItem(itemId) {
        if (!itemId || !Array.isArray(this.items)) {
            return;
        }

        const target = this.items.find((item) => item && item.config && item.config.id === itemId);

        if (!target || !target.element) {
            return;
        }

        target.element.classList.remove('experience-map__item--hidden');

        gsap.killTweensOf(target.element);

        gsap.fromTo(
            target.element,
            {
                autoAlpha : 0,
                scale     : 0.9
            },
            {
                autoAlpha : 1,
                scale     : 1,
                duration  : 0.4,
                ease      : 'sine.out'
            }
        );
    }

    positionTooltipAtPoint({
        clientX,
        clientY
    }) {
        if (!this.tooltipElement) {
            return;
        }

        const rootRect = this.rootElement.getBoundingClientRect();

        const clampedClientX = Math.min(
            Math.max(clientX, rootRect.left),
            rootRect.right
        );
        const clampedClientY = Math.min(
            Math.max(clientY, rootRect.top),
            rootRect.bottom
        );

        // Position in pixels relative to root element
        const leftPx = clampedClientX - rootRect.left;

        // Store original position for arrow offset calculation
        const leftPercent = (leftPx / rootRect.width) * 100;
        this.tooltipElement.dataset.originalLeftPercent = leftPercent;

        // Set horizontal position first
        this.tooltipElement.style.left = `${leftPx}px`;

        // Get tooltip height to position it above the cursor
        const tooltipHeight = this.tooltipElement.offsetHeight || 100; // fallback to 100px
        const topPx         = clampedClientY - rootRect.top - tooltipHeight - 10; // 10px gap above cursor

        this.tooltipElement.style.top = `${topPx}px`;

        // Debug logging
        if (localStorage.getItem('experienceDebug') === '1') {
            // eslint-disable-next-line no-console
            console.log('[ExperienceMap] Tooltip position:', {
                clientX,
                clientY,
                clampedClientX,
                clampedClientY,
                rootRect : {
                    left   : rootRect.left,
                    top    : rootRect.top,
                    width  : rootRect.width,
                    height : rootRect.height
                },
                tooltipHeight,
                leftPx,
                topPx,
                leftPercent
            });
        }

        // Wait for browser to render tooltip with final dimensions before adjusting bounds
        requestAnimationFrame(() => {
            this.adjustTooltipPositionWithinBounds();
        });
    }

    positionTooltipAboveElement(element) {
        if (!element) {
            return;
        }

        const rect    = element.getBoundingClientRect();
        const centerX = rect.left + rect.width / 2;
        const topY    = rect.top;

        this.positionTooltipAtPoint({
            clientX : centerX,
            clientY : topY
        });
    }

    adjustTooltipPositionWithinBounds() {
        if (!this.tooltipElement || !this.rootElement) {
            return;
        }

        const padding = 8;

        this.tooltipElement.classList.remove(
            'experience-map__tooltip--below',
            'experience-map__tooltip--align-left',
            'experience-map__tooltip--align-right'
        );

        const rootRect    = this.rootElement.getBoundingClientRect();
        const tooltipRect = this.tooltipElement.getBoundingClientRect();

        const shouldPlaceBelow = tooltipRect.top < rootRect.top + padding;
        const shouldAlignLeft  = tooltipRect.left < rootRect.left + padding;
        const shouldAlignRight = tooltipRect.right > rootRect.right - padding;

        // Get original click position in pixels relative to root
        const originalLeftPercent = parseFloat(this.tooltipElement.dataset.originalLeftPercent) || 50;
        const originalLeftPx      = (originalLeftPercent / 100) * rootRect.width;

        if (shouldPlaceBelow) {
            this.tooltipElement.classList.add('experience-map__tooltip--below');
        }

        if (shouldAlignLeft && !shouldAlignRight) {
            this.tooltipElement.classList.add('experience-map__tooltip--align-left');
        } else if (shouldAlignRight && !shouldAlignLeft) {
            this.tooltipElement.classList.add('experience-map__tooltip--align-right');
        }

        // Calculate arrow position relative to tooltip's actual position
        // Arrow should point to original click position
        const tooltipLeftRelativeToRoot = tooltipRect.left - rootRect.left;
        const arrowOffsetPx             = originalLeftPx - tooltipLeftRelativeToRoot;
        const arrowOffsetPercent        = (arrowOffsetPx / tooltipRect.width) * 100;

        // Set CSS variable for arrow positioning
        this.tooltipElement.style.setProperty('--tooltip-arrow-offset', `${arrowOffsetPercent}%`);

        // Debug logging
        if (localStorage.getItem('experienceDebug') === '1') {
            // eslint-disable-next-line no-console
            console.log('[ExperienceMap] Tooltip bounds adjustment:', {
                rootRect : {
                    left   : rootRect.left,
                    top    : rootRect.top,
                    right  : rootRect.right,
                    bottom : rootRect.bottom
                },
                tooltipRect : {
                    left   : tooltipRect.left,
                    top    : tooltipRect.top,
                    right  : tooltipRect.right,
                    bottom : tooltipRect.bottom,
                    width  : tooltipRect.width
                },
                shouldPlaceBelow,
                shouldAlignLeft,
                shouldAlignRight,
                originalLeftPercent,
                originalLeftPx,
                tooltipLeftRelativeToRoot,
                arrowOffsetPx,
                arrowOffsetPercent,
                arrowOffset : `${arrowOffsetPercent}%`
            });
        }
    }

    showTooltipForItem({
        itemConfig,
        element,
        triggerEvent
    }) {
        if (!this.tooltipElement || !itemConfig) {
            return;
        }

        if (
            triggerEvent
            && typeof triggerEvent.clientX === 'number'
            && typeof triggerEvent.clientY === 'number'
        ) {
            this.positionTooltipAtPoint({
                clientX : triggerEvent.clientX,
                clientY : triggerEvent.clientY
            });
        } else {
            this.positionTooltipAboveElement(element);
        }

        if (this.tooltipTitleElement) {
            this.tooltipTitleElement.innerHTML = itemConfig.label || '';
        }

        if (this.tooltipTextElement) {
            this.tooltipTextElement.innerHTML = itemConfig.description || '';
        }

        this.tooltipElement.setAttribute('aria-hidden', 'false');

        gsap.killTweensOf(this.tooltipElement);

        gsap.to(this.tooltipElement, {
            autoAlpha : 1,
            duration  : 0.25,
            ease      : 'sine.out'
        });
    }

    hideTooltip() {
        if (!this.tooltipElement) {
            return;
        }

        this.tooltipElement.setAttribute('aria-hidden', 'true');

        gsap.killTweensOf(this.tooltipElement);

        gsap.to(this.tooltipElement, {
            autoAlpha : 0,
            duration  : 0.2,
            ease      : 'sine.out'
        });
    }
}
